import shipmentImg from "assets/img/shipment.svg"

import React, { FC, useState } from "react"
import { useTranslation } from "react-i18next"

import ShippingOrder, { ShippingOrderStatus, ShippingOrderType } from "models/ShippingOrder"
import useShippingOrderModal from "./hooks/useShippingOrderModal"
import AlertModal from "components/AlertModal/AlertModal"
import Reservation from "models/Reservation"
import { getShippingOrderTypeLabel } from "utils/utils"
import { getTranslation } from "utils/translations"
import Tabs from "components/Tabs/Tabs"
import TabsItem from "components/Tabs/TabsItem/TabsItem"
import ShippingOrderModalShippingResult from "./ShippingOrderModalShippingResult"
import Text from "components/Text/Text"
import ShippingOrderModalPlannedAssets from "./ShippingOrderModalPlannedAssets"
import ShippingOrderModalBasicData from "./ShippingOrderModalBasicData"
import { Flex } from "components/Flex"
import Button from "components/Button/Button"

export interface IShippingOrderModalProps {
    reservation: Reservation
    shippingOrder: ShippingOrder
    onClose(): void
}

enum ShippingOrderTab {
    basicData,
    shippingOrder,
    shippingResult,
}

const ShippingOrderModal: FC<React.PropsWithChildren<IShippingOrderModalProps>> = ({
    reservation,
    shippingOrder,
    onClose,
}) => {
    const { t } = useTranslation()
    const [currentTab, setCurrentTab] = useState<ShippingOrderTab>(ShippingOrderTab.basicData)

    const {
        deliveryTerm,
        responsibleWarehouse,
        shippingOrderAssetsExtended,
        shippingOrderAssetIds,
        shippingOrderAssetsSort,
        setShippingOrderAssetsSort,
        shippingResultAssetsSort,
        setShippingResultAssetsSort,
        isLoadingAssetDisplayStatuses,
    } = useShippingOrderModal({
        reservation,
        shippingOrder,
    })

    const modalTitle = `${getShippingOrderTypeLabel(shippingOrder.type)} (${shippingOrder.number})`

    return (
        <AlertModal
            title={modalTitle}
            onClose={onClose}
            maxWidth="1440px"
            theme="secondary"
            buttonLabel={t('common.ok')}>
            <Tabs
                onSwitchTab={tabIndex => setCurrentTab(tabIndex)}
                toolbar={currentTab === ShippingOrderTab.shippingOrder && shippingOrderAssetsSort
                    ? <Flex alignItems="center" className="h-100">
                        <Button
                            theme="link"
                            label={t('shippingOrders.resetToDefaultOrder')}
                            onClick={() => setShippingOrderAssetsSort(null)}
                        />
                    </Flex>
                    : null
                }>
                <TabsItem label={t('shippingOrders.basicData')}>
                    <ShippingOrderModalBasicData
                        deliveryTermDescription={getTranslation(deliveryTerm?.termDescription)}
                        responsibleWarehouseDescription={responsibleWarehouse?.description}
                        departureDate={shippingOrder.departureDate || null}
                        departureDateMin={shippingOrder.departureDateMin || null}
                        arrivalDate={shippingOrder?.arrivalDate || null}
                        arrivalDateMax={shippingOrder.arrivalDateMax || null}
                        note={shippingOrder.note}
                        deliveryNoteHeaderText={shippingOrder.deliveryNoteHeaderText}
                        deliveryNoteFooterText={shippingOrder.deliveryNoteFooterText}
                        deliveryNotePrintFeatures={shippingOrder.deliveryNotePrintFeatures}
                        showDeliveryNoteTextSection={shippingOrder.type === ShippingOrderType.delivery}
                        personInCharge={shippingOrder.personInCharge}
                        personInChargePhone={shippingOrder.personInChargePhone}
                    />
                </TabsItem>
                <TabsItem label={t('shippingOrders.shippingOrder')}>
                    {shippingOrderAssetsExtended.length > 0 &&
                        <div className="overflow-auto">
                            <ShippingOrderModalPlannedAssets
                                locations={shippingOrder.locations}
                                sort={shippingOrderAssetsSort}
                                setSort={setShippingOrderAssetsSort}
                                assets={shippingOrderAssetsExtended}
                                isLoadingAssetDisplayStatuses={isLoadingAssetDisplayStatuses}
                            />
                        </div>
                    }
                </TabsItem>
                <TabsItem label={t('shippingOrders.shippingResult')}>
                    {shippingOrder.status === ShippingOrderStatus.completed
                        ? (shippingOrderAssetIds.length > 0
                            ? <div className="overflow-auto">
                                <ShippingOrderModalShippingResult
                                    shippingOrderType={shippingOrder.type}
                                    sort={shippingResultAssetsSort}
                                    setSort={setShippingResultAssetsSort}
                                    shippedAssets={shippingOrderAssetsExtended}
                                />
                            </div>
                            : null
                        ) : (
                            <div className="overflow-auto p-4 text-center">
                                <img src={shipmentImg} alt="" className="d-inline-block mb-4" />
                                <Text preLine>{t('shippingOrders.noShippingResult')}</Text>
                            </div>
                        )
                    }
                </TabsItem>
            </Tabs>
        </AlertModal>
    )
}

export default ShippingOrderModal
